/* tslint:disable */
/* eslint-disable */
/**
 * Ampli - A strong typed wrapper for your Analytics
 *
 * This file is generated by Amplitude.
 * To update run 'ampli pull web'
 *
 * Required dependencies: @amplitude/analytics-browser@^1.3.0
 * Tracking Plan Version: 6
 * Build: 1.0.0
 * Runtime: browser:typescript-ampli-v2
 *
 * [View Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest)
 *
 * [Full Setup Instructions](https://data.amplitude.com/swaq/SWAQ/implementation/web)
 */

import * as amplitude from '@amplitude/analytics-browser';

export type Environment = 'production' | 'development' | 'staging';

export const ApiKey: Record<Environment, string> = {
  production: '',
  development: '',
  staging: ''
};

/**
 * Default Amplitude configuration options. Contains tracking plan information.
 */
export const DefaultConfiguration: BrowserOptions = {
  plan: {
    version: '6',
    branch: 'main',
    source: 'web',
    versionId: 'b77d4e64-c26d-4ded-b828-1031b625133a'
  },
  ...{
    ingestionMetadata: {
      sourceName: 'browser-typescript-ampli',
      sourceVersion: '2.0.0'
    }
  }
};

export interface ButtonClickCTAWatchVideoProperties {
  merchantId: number | undefined;
  merchantName: string| undefined;
  callToAction: string | null | undefined;
}

export interface LoadOptionsBase { disabled?: boolean }

export type LoadOptionsWithEnvironment = LoadOptionsBase & { environment: Environment; client?: { configuration?: BrowserOptions; }; };
export type LoadOptionsWithApiKey = LoadOptionsBase & { client: { apiKey: string; configuration?: BrowserOptions; } };
export type LoadOptionsWithClientInstance = LoadOptionsBase & { client: { instance: BrowserClient; } };

export type LoadOptions = LoadOptionsWithEnvironment | LoadOptionsWithApiKey | LoadOptionsWithClientInstance;

export class PageviewActivation implements BaseEvent {
  event_type = 'pageview: activation';
}

export class PageviewCreateAccount implements BaseEvent {
  event_type = 'pageview: create account';
}

export class PageviewCustomization implements BaseEvent {
  event_type = 'pageview: customization';
}

export class PageviewLogIn implements BaseEvent {
  event_type = 'pageview: log in';
}

export class PageviewReview implements BaseEvent {
  event_type = 'pageview: review';
}

export class PageviewSend implements BaseEvent {
  event_type = 'pageview: send';
}

export class PageviewUploadVideo implements BaseEvent {
  event_type = 'pageview: upload video';
}

export class PageviewUserLibrary implements BaseEvent {
  event_type = 'pageview: user library';
}

export class ButtonClickCTAWatchVideo implements BaseEvent {
  event_type = 'buttonclick: call to action in watch video';
  event_properties = {}

  constructor(event_properties: ButtonClickCTAWatchVideoProperties) {
    this.event_properties = event_properties;
  }
}

export class ButtonClickShareWatchVideo implements BaseEvent {
  event_type = 'buttonclick: share video from watch video';
}

export class ButtonClickShareLibrary implements BaseEvent {
  event_type = 'buttonclick: share video from library';
}

export type PromiseResult<T> = { promise: Promise<T | void> };

const getVoidPromiseResult = () => ({ promise: Promise.resolve() });

// prettier-ignore
export class Ampli {
  private disabled: boolean = false;
  private amplitude?: BrowserClient;

  get client(): BrowserClient {
    this.isInitializedAndEnabled();
    return this.amplitude!;
  }

  get isLoaded(): boolean {
    return this.amplitude != null;
  }

  private isInitializedAndEnabled(): boolean {
    if (!this.amplitude) {
      console.error('ERROR: Ampli is not yet initialized. Have you called ampli.load() on app start?');
      return false;
    }
    return !this.disabled;
  }

  /**
   * Initialize the Ampli SDK. Call once when your application starts.
   *
   * @param options Configuration options to initialize the Ampli SDK with.
   */
  load(options: LoadOptions): PromiseResult<void> {
    this.disabled = options.disabled ?? false;

    if (this.amplitude) {
      console.warn('WARNING: Ampli is already intialized. Ampli.load() should be called once at application startup.');
      return getVoidPromiseResult();
    }

    let apiKey: string | null = null;
    if (options.client && 'apiKey' in options.client) {
      apiKey = options.client.apiKey;
    } else if ('environment' in options) {
      apiKey = ApiKey[options.environment];
    }

    if (options.client && 'instance' in options.client) {
      this.amplitude = options.client.instance;
    } else if (apiKey) {
      this.amplitude = amplitude.createInstance();
      return this.amplitude.init(apiKey, undefined, { ...DefaultConfiguration, ...options.client?.configuration });
    } else {
      console.error("ERROR: ampli.load() requires 'environment', 'client.apiKey', or 'client.instance'");
    }

    return getVoidPromiseResult();
  }

  /**
   * Identify a user and set user properties.
   *
   * @param userId The user's id.
   * @param options Optional event options.
   */
  identify(
    userId: string | undefined,
    options?: EventOptions,
  ): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    if (userId) {
      options = {...options,  user_id: userId};
    }

    const amplitudeIdentify = new amplitude.Identify();
    return this.amplitude!.identify(
      amplitudeIdentify,
      options,
    );
  }

  /**
   * Track event
   *
   * @param event The event to track.
   * @param options Optional event options.
   */
  track(event: Event, options?: EventOptions): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.track(event, undefined, options);
  }

  /**
   * pageview: activation
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20activation)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewActivation(
    options?: EventOptions,
  ) {
    return this.track(new PageviewActivation(), options);
  }

  /**
   * pageview: create account
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20create%20account)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewCreateAccount(
    options?: EventOptions,
  ) {
    return this.track(new PageviewCreateAccount(), options);
  }

  /**
   * pageview: customization
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20customization)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewCustomization(
    options?: EventOptions,
  ) {
    return this.track(new PageviewCustomization(), options);
  }

  /**
   * pageview: log in
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20log%20in)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewLogIn(
    options?: EventOptions,
  ) {
    return this.track(new PageviewLogIn(), options);
  }

  /**
   * pageview: review
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20review)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewReview(
    options?: EventOptions,
  ) {
    return this.track(new PageviewReview(), options);
  }

  /**
   * pageview: send
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20send)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewSend(
    options?: EventOptions,
  ) {
    return this.track(new PageviewSend(), options);
  }

  /**
   * pageview: upload video
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20upload%20video)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewUploadVideo(
    options?: EventOptions,
  ) {
    return this.track(new PageviewUploadVideo(), options);
  }

  /**
   * pageview: user library
   *
   * [View in Tracking Plan](https://data.amplitude.com/swaq/SWAQ/events/main/latest/pageview%3A%20user%20library)
   *
   * Owner: Jesus Mañez
   *
   * @param options Amplitude event options.
   */
  pageviewUserLibrary(
    options?: EventOptions,
  ) {
    return this.track(new PageviewUserLibrary(), options);
  }

  /**
   * pageview: user library
   *
   * [View in Tracking Plan](https://app.amplitude.com/data/swaq/SWAQ/events/main/latest/buttonclick%3A%20call%20to%20action%20in%20watch%20video?view=All&tab=DETAILS&propertyValidityFilter=All%2520Properties)
   *
   * Owner: Matias Arrech
   *
   * @param options Amplitude event options.
   */
  buttonClickCTAWatchVideo(
    properties: ButtonClickCTAWatchVideoProperties,
    options?: EventOptions,
  ) {
    return this.track(new ButtonClickCTAWatchVideo( properties ), options);
  }

  /**
   * pageview: user library
   *
   * [View in Tracking Plan](https://app.amplitude.com/data/swaq/SWAQ/events/main/latest/buttonclick%3A%20share%20video%20from%20watch%20video?view=All&tab=DETAILS&propertyValidityFilter=All%2520Properties)
   *
   * Owner: Matias Arrech
   *
   * @param options Amplitude event options.
   */
  buttonClickShareWatchVideo(
    options?: EventOptions,
  ) {
    return this.track(new ButtonClickShareWatchVideo(), options);
  }

  /**
   * pageview: user library
   *
   * [View in Tracking Plan](https://app.amplitude.com/data/swaq/SWAQ/events/main/latest/buttonclick%3A%20share%20video%20from%20library?view=All&tab=DETAILS&propertyValidityFilter=All%2520Properties)
   *
   * Owner: Matias Arrech
   *
   * @param options Amplitude event options.
   */
  buttonClickShareLibrary(
    options?: EventOptions,
  ) {
    return this.track(new ButtonClickShareLibrary(), options);
  }

}

export const ampli = new Ampli();

// BASE TYPES
type BrowserOptions = amplitude.Types.BrowserOptions;

export type BrowserClient = amplitude.Types.BrowserClient;
export type BaseEvent = amplitude.Types.BaseEvent;
export type IdentifyEvent = amplitude.Types.IdentifyEvent;
export type GroupEvent = amplitude.Types.GroupIdentifyEvent;
export type Event = amplitude.Types.Event;
export type EventOptions = amplitude.Types.EventOptions;
export type Result = amplitude.Types.Result;
